import type { KeyboardEvent } from "react";

/** Clavier d'un groupe de cartes de choix (`ChoiceCard radio` dans un `role="radiogroup"`), comme un groupe radio
 *  natif : flèches pour passer au choix voisin (en bouclant), Début/Fin pour le premier/dernier ; les cartes
 *  désactivées sont sautées ; une seule carte tabulable (la choisie, à défaut la première active). */

const STEPS: Record<string, number> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

/** Index du choix visé par la touche `key` depuis `current` (−1 si aucun), ou `null` si la touche ne concerne pas
 *  le groupe ou qu'aucun choix n'est actif. */
export function nextRadioIndex(enabled: readonly boolean[], current: number, key: string): number | null {
  const active = enabled.map((on, i) => (on ? i : -1)).filter((i) => i >= 0);
  if (active.length === 0) return null;
  if (key === "Home") return active[0]!;
  if (key === "End") return active[active.length - 1]!;
  const step = STEPS[key];
  if (!step) return null;
  const n = enabled.length;
  let i = current < 0 ? (step > 0 ? -1 : n) : current;
  for (let tries = 0; tries < n; tries++) {
    i = (i + step + n) % n;
    if (enabled[i]) return i;
  }
  return null;
}

/** `tabIndex` d'une carte : 0 pour la carte choisie si elle est active, sinon pour la première active. */
export function rovingTabIndex(index: number, selected: number, enabled: readonly boolean[]): 0 | -1 {
  const target = selected >= 0 && enabled[selected] ? selected : enabled.indexOf(true);
  return index === target ? 0 : -1;
}

/** Gestionnaire `onKeyDown` à poser sur le `role="radiogroup"` : choisit la carte voisine et lui donne le focus. */
export function onRadioGroupKeyDown(event: KeyboardEvent<HTMLElement>, onSelect: (index: number) => void): void {
  const radios = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
  const enabled = radios.map((r) => !(r as HTMLButtonElement).disabled && r.getAttribute("aria-disabled") !== "true");
  const focused = radios.indexOf(document.activeElement as HTMLElement);
  const current = focused >= 0 ? focused : radios.findIndex((r) => r.getAttribute("aria-checked") === "true");
  const next = nextRadioIndex(enabled, current, event.key);
  if (next === null) return;
  event.preventDefault();
  onSelect(next);
  radios[next]?.focus();
}
